import { PieChart } from '@mui/x-charts/PieChart'
import { Typography, CircularProgress, Box, Grid, Paper, colors } from '@mui/material'
import { InfoRounded } from "@mui/icons-material"
import { useGetReservationDashboardQuery } from 'store/dashboard'
import ChartPanel from './ChartPanel'
import PieCenterLabel from './PieCenterLabel'
import PieLegendList from './PieLegendList'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'
import { STATUS_COLORS } from './chartDefaults'
import RevenueByStatusChart from './RevenueByStatusChart'
import ServiceTypeMixChart from './ServiceTypeMixChart'
import RevenueByPackageChart from './RevenueByPackageChart'
import StatusByServiceTypeChart from './StatusByServiceTypeChart'
import CompletionRateChart from './CompletionRateChart'
import ServiceCategoryMixChart from './ServiceCategoryMixChart'
import DashboardSummaryCards from "./DashboardSummaryCards"
import ChartTitle from "./ChartTitle"

const BookingServiceChart = () => {
  const { startDate, endDate } = useTimeRange()

  const { data, isLoading, isError, refetch, isFetching } = useGetReservationDashboardQuery({
    startDate,
    endDate
  })

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 'none'
      }}
      elevation={0}
    >
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <NoDataAvailable variant='error' onRefresh={refetch} />
      ) : !data ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : (
        <>
          {/* Row 1: four panel cards */}
          <ChartTitle>Booking Service</ChartTitle>
          <DashboardSummaryCards />
          <Grid container spacing={2} mb={4} alignItems='stretch'>
            <Grid item xs={3}>
              <ChartPanel title='Booking Status Summary' tooltip='Displays the distribution of reservations grouped by booking status.'>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <PieChart
                    series={[
                      {
                        innerRadius: 50,
                        outerRadius: 80,
                        cx: 100,
                        cy: 100,
                        data: (data.statusSummary ?? []).map((item, idx) => ({
                          id: idx,
                          value: item.count,
                          color: STATUS_COLORS[item.status] ?? '#607D8B'
                        }))
                      }
                    ]}
                    width={200}
                    height={200}
                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                    slotProps={{ legend: { hidden: true } }}
                  >
                    <PieCenterLabel primary='Total Booking' secondary={data.totalBooking.toLocaleString('id-ID')} />
                  </PieChart>
                </Box>
                <PieLegendList
                  items={(data.statusSummary ?? []).map((item) => ({
                    color: STATUS_COLORS[item.status] ?? '#607D8B',
                    label: item.status,
                    value: `${item.percentage.toFixed(1)}%`
                  }))}
                />
                <Box display='flex' alignItems={'center'} sx={{ bgcolor: colors.blue[50], borderRadius: 1, p: 1, mt: 2 }}>
                  <InfoRounded sx={{ color: colors.blue[800], fontSize: 16, mr: 1 }} />
                  <Typography variant='body2' sx={{ fontWeight: 'bold', color: colors.blue[800] }}>
                    Closure Rate: {(data.conversionRate ?? 0).toFixed(1)}% (Completed/Total)
                  </Typography>
                </Box>
              </ChartPanel>
            </Grid>

            <Grid item xs={3}>
              <ChartPanel title='Revenue By Status' tooltip="Displays the revenue contribution of reservations grouped by their current status.">
                <RevenueByStatusChart data={data.revenueByStatus ?? []} />
              </ChartPanel>
            </Grid>

            <Grid item xs={3}>
              <ChartPanel title='Service Type Mix (By Volume)' tooltip='Displays the booking volume distribution by service type.'>
                <ServiceTypeMixChart data={data.serviceCategorySummary ?? []} />
              </ChartPanel>
            </Grid>

            <Grid item xs={3}>
              <ChartPanel title='Revenue Contribution (By Type)' tooltip='Displays the revenue contribution of bookings grouped by service type.'>
                <RevenueByPackageChart data={data.revenueByServicePackage ?? []} />
              </ChartPanel>
            </Grid>
          </Grid>

          {/* Row 2: Status by Service Type / Completion Rate / Service Category Mix */}
          <Grid container spacing={2} alignItems='stretch'>
            <Grid item xs={5}>
              <ChartPanel title='Status by Service Package (By Volume)' tooltip='Displays the booking volume status distribution across each service type.'>
                <StatusByServiceTypeChart data={data.statusByServiceType ?? []} />
              </ChartPanel>
            </Grid>
            <Grid item xs={3.5}>
              <ChartPanel title='Completion Rate by Service Type' tooltip='Displays the completion rate achieved by each service type.'>
                <CompletionRateChart data={data.completionRateByServicePackage ?? []} />
              </ChartPanel>
            </Grid>
            <Grid item xs={3.5}>
              <ChartPanel title='Service Category Mix (By Volume)' tooltip='Displays the booking volume distribution across service categories.'>
                <ServiceCategoryMixChart data={data.serviceTypeSummary ?? []} />
              </ChartPanel>
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  )
}

export default BookingServiceChart
